<script setup>
import { ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import LaisModal from 'dashboard/components-next/LaisModal.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import PostSaleDeedFeedbackChoiceGroup from './PostSaleDeedFeedbackChoiceGroup.vue';
import PostSaleDeedFeedbackConfirmation from './PostSaleDeedFeedbackConfirmation.vue';
import PostSaleDeedFeedbackLoading from './PostSaleDeedFeedbackLoading.vue';
import PostSaleDeedFeedbackQuestions from './PostSaleDeedFeedbackQuestions.vue';
import PostSaleDeedFeedbackShell from './PostSaleDeedFeedbackShell.vue';

const props = defineProps({
  catalog: { type: Object, default: null },
  confirmation: { type: Boolean, default: false },
  error: { type: String, default: '' },
  loading: { type: Boolean, default: false },
  open: { type: Boolean, default: false },
  pending: { type: Array, default: () => [] },
  process: { type: Object, default: null },
  proof: { type: Object, default: null },
  showList: { type: Boolean, default: false },
  submitting: { type: Boolean, default: false },
  uploading: { type: Boolean, default: false },
});
const emit = defineEmits(['back', 'confirmed', 'select', 'submit', 'upload']);
const selectedId = ref('');
const { t } = useI18n();

const closeConfirmation = () => {
  if (props.confirmation && !props.submitting) emit('confirmed');
};

watch(
  () => props.pending,
  pending => {
    selectedId.value =
      pending.length === 1 ? String(pending[0].processo_id) : '';
  },
  { immediate: true }
);
</script>

<template>
  <PostSaleDeedFeedbackShell v-if="!confirmation" :open="open">
    <PostSaleDeedFeedbackLoading v-if="loading" />
    <template v-else-if="showList">
      <div data-testid="post-sale-deed-feedback-list">
        <h2
          class="mb-10 font-redHatDisplay text-[32px] font-bold leading-9 text-lais-text"
        >
          {{ t('POST_SALE.DEED_FEEDBACK.LIST.TITLE') }}
        </h2>
        <p class="mb-10 text-xl leading-6 text-lais-text">
          {{ t('POST_SALE.DEED_FEEDBACK.LIST.DESCRIPTION') }}
        </p>
        <p class="mb-11 text-xl font-bold leading-6 text-lais-text">
          {{ t('POST_SALE.DEED_FEEDBACK.LIST.SELECT') }}
        </p>
        <PostSaleDeedFeedbackChoiceGroup
          v-model="selectedId"
          :options="
            pending.map(item => ({
              id: item.processo_id,
              label:
                item.logradouro || t('POST_SALE.COMMON.ADDRESS_UNAVAILABLE'),
            }))
          "
        />
        <footer
          data-testid="post-sale-deed-feedback-list-actions"
          class="fixed bottom-[33px] left-1/2 z-[99] flex w-[calc(100%-2.5rem)] max-w-[725px] -translate-x-1/2 justify-end bg-white py-5 sm:w-[calc(100%-5rem)]"
        >
          <LaisPrimaryButton
            data-testid="post-sale-deed-feedback-list-continue"
            :disabled="!selectedId"
            size="lg"
            @click="emit('select', selectedId)"
          >
            <span class="inline-flex items-center gap-2">
              <span>{{ t('POST_SALE.DEED_FEEDBACK.LIST.CONTINUE') }}</span>
              <span class="i-lucide-arrow-right size-4" aria-hidden="true" />
            </span>
          </LaisPrimaryButton>
        </footer>
      </div>
    </template>
    <div v-else-if="process" data-testid="post-sale-deed-feedback-questions">
      <PostSaleDeedFeedbackQuestions
        :catalog="catalog || {}"
        :process="process"
        :proof="proof"
        :submitting="submitting"
        :uploading="uploading"
        :show-back="pending.length > 1"
        @back="emit('back')"
        @submit="emit('submit', $event)"
        @upload="emit('upload', $event)"
      />
    </div>
    <p
      v-if="error"
      class="mt-10 ml-[19px] text-[0.85rem] font-bold leading-5 text-lais-danger"
    >
      {{ error }}
    </p>
  </PostSaleDeedFeedbackShell>
  <LaisModal
    v-else
    :show="open"
    disable-escape-key-down
    full-screen
    full-screen-layout-class="relative h-[100dvh] max-h-none rounded-none lg:h-auto lg:max-h-[calc(100dvh-2rem)] lg:rounded-lais-xl"
    full-screen-max-width-class="lg:max-w-[1039px]"
    @close="closeConfirmation"
  >
    <PostSaleDeedFeedbackConfirmation @close="closeConfirmation" />
  </LaisModal>
</template>
